import { useHistory, useLocation } from "react-router-dom"
import { useCallback, useEffect, useMemo } from "react"

type BatchParams = {
  key: string
  value: string | null
}[]
type SetOptions = {
  targetPath?: string
  clearKeys?: string[]
  replaceHistory?: boolean
}

export function useQuerySearch() {
  return new URLSearchParams(useLocation().search)
}

// Custom hook to manage search params
export const useSearchParams = (defaultParams?: BatchParams) => {
  // hooks
  const { search } = useLocation()
  const history = useHistory()

  // functions
  // gets the value of a specific search parameter.
  const get = useCallback(
    (key: string): string | null => {
      const url = new URLSearchParams(search)
      return url.get(key)
    },
    [search]
  )

  // sets the value of a specific search parameter.
  const set = useCallback(
    (key: string, value: string | null, options?: SetOptions) => {
      const { targetPath, clearKeys, replaceHistory } = options || {}
      const url = new URLSearchParams(search)
      if (value === null) {
        url.delete(key)
      } else {
        url.set(key, value)
      }
      for (let key of clearKeys || []) {
        url.delete(key)
      }
      const newUrl = (targetPath ?? "") + "?" + url.toString()
      if (replaceHistory) {
        history.replace(newUrl)
      } else {
        history.push(newUrl)
      }
    },
    [search]
  )

  // removes a specific search parameter.
  const removeParam = useCallback(
    (name: string, targetPath?: string, replaceHistory?: boolean) => {
      const url = new URLSearchParams(search)
      url.delete(name)
      const newUrl = (targetPath ?? "") + "?" + url.toString()
      if (replaceHistory) {
        history.replace(newUrl)
      } else {
        history.push(newUrl)
      }
    },
    [search]
  )

  //  sets multiple search parameters in a batch.
  const setBatch = useCallback(
    (batch: BatchParams) => {
      const url = new URLSearchParams(search)
      batch.forEach((entry) => {
        if (entry.value === null) {
          url.delete(entry.key)
        } else {
          url.set(entry.key, entry.value)
        }
      })
      history.push("?" + url.toString())
    },
    [search]
  )

  //  removes multiple search parameters in a batch
  const removeBatch = useCallback(
    (batch: string[]) => {
      const url = new URLSearchParams(search)
      batch.forEach((entry) => {
        url.delete(entry)
      })
      history.push("?" + url.toString())
    },
    [search]
  )

  // set initial search params if provided
  useEffect(() => {
    if (!defaultParams) return
    setBatch(defaultParams)
  }, [defaultParams])

  return useMemo(
    () => ({
      get,
      set,
      removeParam,
      setBatch,
      removeBatch,
      searchParams: search,
    }),
    [get, set, setBatch, search]
  )
}
